import {produce, WritableDraft} from 'immer';

import {DocumentStoreApi} from '@/components/document/store/document-store';
import {ARRAY_META_FIELD_COUNT, STATE_VALUE_WILDCARD_SUFFIX} from '@/constants/engine';
import {isDocumentAccessRecord, isFieldAccessRecord, isGroupAccessRecord, isSectionAccessRecord} from '../dependencies';
import {Model, DocumentModel, ValueResolver, DocumentBaseGroup} from '../types';
import {
  isReferenceToAccount,
  isReferenceToAdvisorProfile,
  isReferenceToComputedAllocationAccount,
  isReferenceToInvestorProfile,
  isReferenceToSourceAccount,
  isReferenceToUserProfile,
} from '../utils/reference';
import {computeField} from './document-field';
import {computeGroup} from './document-group';
import {computeSection} from './document-section';
import {DocumentProcessorUtils, getDocumentProcessorUtils} from './utils';

const ARRAY_ROW_INDEX_REGEX = /#(\d+)$/;

export const computeDocumentModel = (
  model: Model,
  store: DocumentStoreApi,
  valueResolver: ValueResolver,
): DocumentModel => {
  const utils = getDocumentProcessorUtils(store, valueResolver);

  const computedDocumentModel = {
    sections: model.form.sections.map(computeSection(utils)),
    documents: model.form.documents.map(document => ({
      ...document,
      included: utils.computeConditions(document.conditions),
    })),
  };

  // Executes onInit events which are important when re-opening a document
  computedDocumentModel.sections.forEach(section => {
    section.groups.forEach(group => {
      if ('rows' in group) {
        group.rows.forEach(row => {
          row.onInit.forEach(event => event());
        });
      } else {
        group.onInit.forEach(event => event());
      }
    });
  });

  // Recompute all the fields to set the correct visibility now that the dependencyMap is initialized
  const keys = model.dependencyMap.graph.overallOrder();
  const fieldUpdater = createFieldUpdater(utils, model, true);
  return produce(computedDocumentModel, draft => {
    for (const id of keys) {
      fieldUpdater(draft, id);
    }
  });
};

export const updateDocumentModel = (
  document: DocumentModel,
  keysToUpdate: string[],
  model: Model,
  store: DocumentStoreApi,
  valueResolver: ValueResolver,
): DocumentModel => {
  return produce(document, draft => {
    const utils = getDocumentProcessorUtils(store, valueResolver, document);

    const fieldUpdater = createFieldUpdater(utils, model);

    for (const id of keysToUpdate) {
      if (utils.processedIds.has(id)) continue;

      const accessRecord = model.accessMap.get(id);

      if (!accessRecord) throw new Error(`Access record not found for ${id}`);

      fieldUpdater(draft, id);
    }
  });
};

export const createFieldUpdater = (utils: DocumentProcessorUtils, model: Model, initialLoading: boolean = false) =>
  function processField(draft: WritableDraft<DocumentModel>, id: string) {
    if (utils.processedIds.has(id)) return;
    utils.processedIds.add(id);

    // If the field has a wild selector, process all fields
    if (id.endsWith(STATE_VALUE_WILDCARD_SUFFIX)) {
      const wildSelector = id.slice(0, -1);
      const keys = Array.from(model.accessMap.keys()).filter(key => key.startsWith(wildSelector));
      for (const key of keys) {
        processField(draft, key);
      }
      return;
    }

    // If the field has no dependencies or it is the initial loading
    if (initialLoading || !model.dependencyMap.graph.hasNode(id)) {
      return computeElement(utils, model, draft, id);
    }

    const relatedQueryField = id.includes('#') ? id.split('#')[0] + '#*' : undefined;
    const hasQueryRelatedDependencies = !!relatedQueryField && model.dependencyMap.graph.hasNode(relatedQueryField);

    // Currently processed field depends on other fields
    computeElements(utils, model, draft, [
      ...model.dependencyMap.graph.dependenciesOf(id),
      ...(hasQueryRelatedDependencies ? model.dependencyMap.graph.dependenciesOf(relatedQueryField) : []),
    ]);

    computeElement(utils, model, draft, id);

    // Field that depends on the current processed field
    computeElements(utils, model, draft, [
      ...model.dependencyMap.graph.dependantsOf(id).reverse(),
      ...(hasQueryRelatedDependencies ? model.dependencyMap.graph.dependantsOf(relatedQueryField).reverse() : []),
    ]);
  };

export const computeElements = (
  utils: DocumentProcessorUtils,
  model: Model,
  draft: WritableDraft<DocumentModel>,
  ids: string[],
): void => {
  ids.forEach(id => {
    if (!utils.processedIds.has(id)) {
      computeElement(utils, model, draft, id);
    }
  });
};

const getArrayRowIndex = (id: string): number | undefined => {
  const match = ARRAY_ROW_INDEX_REGEX.exec(id);
  return match ? Number(match[1]) : undefined;
};

export const computeElement = (
  utils: DocumentProcessorUtils,
  model: Model,
  draft: WritableDraft<DocumentModel>,
  id: string,
): void => {
  const accessRecord = model.accessMap.get(id);

  if (!accessRecord) {
    // Array rows' fields' accessRecords are initialized only when the array group is processed
    if (id.includes('#') || id.includes(ARRAY_META_FIELD_COUNT) || id.endsWith(STATE_VALUE_WILDCARD_SUFFIX)) return;

    // Advisor profile and Investor profile fields are not in the accessMap
    if (isReferenceToUserProfile(id) || isReferenceToAdvisorProfile(id) || isReferenceToInvestorProfile(id)) return;

    // Account and source account constants are not in the accessMap
    if (isReferenceToAccount(id) || isReferenceToSourceAccount(id)) return;

    // Accounts' fields from the predetermined allocations plugin are not in the accessMap
    if (isReferenceToComputedAllocationAccount(id)) return;

    throw new Error(`Access record not found for ${id}`);
  }

  // Field: recompute only the field (or its array group when needed)
  if (isFieldAccessRecord(accessRecord)) {
    const {sectionIndex, groupIndex, fieldIndex} = accessRecord;

    const sectionModel = model.form.sections[sectionIndex];
    const groupModel = sectionModel.groups[groupIndex];

    // If array group
    if ('type' in groupModel) {
      const draftGroup = draft.sections[sectionIndex].groups[groupIndex];
      const rowIndex = getArrayRowIndex(id);

      // Fast path: row field of an array whose row count hasn't changed
      if (
        rowIndex !== undefined &&
        'rows' in draftGroup &&
        draftGroup.rows.length === groupModel.rowCount(utils.valueResolver, utils.visibilitySet)
      ) {
        // Row no longer exists, nothing to update
        if (rowIndex >= draftGroup.rows.length) return;

        const rowModel = groupModel.row(rowIndex);
        const fieldModel = rowModel.fields[fieldIndex];

        // Field still at the same position in the row: update it in place
        if (fieldModel?.id === id) {
          draftGroup.rows[rowIndex].fields[fieldIndex] = computeField(utils, rowModel.id)(fieldModel);
          return;
        }
      }

      // Row count changed or field moved: rebuild the whole array group
      draft.sections[accessRecord.sectionIndex].groups[accessRecord.groupIndex] = computeGroup(
        utils,
        sectionModel.id,
      )(groupModel);
    } else {
      // Base group: recompute the single field
      const fieldModel = groupModel.fields[fieldIndex];

      (draft.sections[accessRecord.sectionIndex].groups[accessRecord.groupIndex] as DocumentBaseGroup).fields[
        accessRecord.fieldIndex
      ] = computeField(utils, groupModel.id)(fieldModel);
    }
  } else if (isGroupAccessRecord(accessRecord)) {
    // Group: recompute the whole group
    const {sectionIndex, groupIndex} = accessRecord;
    const sectionModel = model.form.sections[sectionIndex];
    const groupModel = sectionModel.groups[groupIndex];
    draft.sections[sectionIndex].groups[groupIndex] = computeGroup(utils, sectionModel.id)(groupModel);
  } else if (isSectionAccessRecord(accessRecord)) {
    // Section: recompute the whole section
    const {sectionIndex} = accessRecord;
    const sectionModel = model.form.sections[sectionIndex];
    draft.sections[sectionIndex] = computeSection(utils)(sectionModel);
  } else if (isDocumentAccessRecord(accessRecord)) {
    // Document: only re-evaluate its inclusion conditions
    const documentModel = model.form.documents[accessRecord.documentIndex];
    draft.documents[accessRecord.documentIndex] = {
      ...documentModel,
      included: utils.computeConditions(documentModel.conditions),
    };
  }
};
